import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Power, Tag, Percent, Euro, Truck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const TYPE_LABELS = {
  percent: { label: "Prozent (%)", Icon: Percent },
  fixed: { label: "Fester Betrag (€)", Icon: Euro },
  free_shipping: { label: "Kostenloser Versand", Icon: Truck },
};

const emptyForm = { code: "", type: "percent", value: "", active: true };

export default function DiscountsView() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/discount-codes");
      setCodes(data);
    } catch (e) { toast.error("Rabattcodes konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Bitte einen Code eingeben."); return; }
    if (form.type !== "free_shipping" && (!form.value || Number(form.value) <= 0)) {
      toast.error("Bitte einen gültigen Wert eingeben."); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/discount-codes", {
        code: form.code.trim(), type: form.type,
        value: form.type === "free_shipping" ? 0 : Number(form.value),
        active: true,
      });
      setCodes((p) => [data, ...p]);
      setForm(emptyForm);
      toast.success(`Rabattcode ${data.code} angelegt.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      const { data } = await api.patch(`/discount-codes/${c.id}`, { active: !c.active });
      setCodes((p) => p.map((x) => (x.id === c.id ? data : x)));
      toast.success(data.active ? "Code aktiviert." : "Code deaktiviert.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/discount-codes/${id}`);
      setCodes((p) => p.filter((x) => x.id !== id));
      toast.success("Rabattcode gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const valueLabel = (c) => {
    if (c.type === "percent") return `${c.value}%`;
    if (c.type === "fixed") return `${c.value.toFixed(2)} €`;
    return "Kostenloser Versand";
  };

  return (
    <div data-testid="discounts-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-2">Rabattcodes</h1>
      <p className="text-sm text-[#888] mb-8 max-w-2xl">
        Codes, die Kunden beim Beauftragen einer Reparatur eingeben können — als Prozent-Rabatt, fester €-Betrag oder kostenloser Versand.
      </p>

      <form onSubmit={create} data-testid="discount-create-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Code</label>
            <input data-testid="discount-code-input" value={form.code} onChange={set("code")} placeholder="z.B. SOMMER10" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Typ</label>
            <select data-testid="discount-type-select" value={form.type} onChange={set("type")} className={inputCls}>
              <option value="percent" className="bg-[#0a0a0a]">Prozent (%)</option>
              <option value="fixed" className="bg-[#0a0a0a]">Fester Betrag (€)</option>
              <option value="free_shipping" className="bg-[#0a0a0a]">Kostenloser Versand</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Wert</label>
            <input data-testid="discount-value-input" type="number" min="0" step="0.5" disabled={form.type === "free_shipping"}
              value={form.type === "free_shipping" ? "" : form.value} onChange={set("value")}
              placeholder={form.type === "percent" ? "z.B. 10" : form.type === "fixed" ? "z.B. 5" : "—"}
              className={`${inputCls} ${form.type === "free_shipping" ? "opacity-40 cursor-not-allowed" : ""}`} />
          </div>
          <button type="submit" disabled={saving} data-testid="discount-create-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] px-5 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            <Plus size={16} /> {saving ? "Speichern…" : "Code anlegen"}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : codes.length === 0 ? (
        <div data-testid="discounts-empty-state" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <p className="text-[#888]">Noch keine Rabattcodes angelegt.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="discounts-list">
          {codes.map((c) => {
            const { Icon } = TYPE_LABELS[c.type] || TYPE_LABELS.percent;
            return (
              <div key={c.id} data-testid={`discount-${c.id}`}
                className={`rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${c.active ? "border-white/10 bg-[#0a0a0a]" : "border-red-500/20 bg-[#0a0a0a]/60 opacity-60"}`}>
                <div className="flex items-center gap-3">
                  <Tag size={16} className="text-[#666]" />
                  <span className="font-mono text-lg text-white">{c.code}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-[#ccc] inline-flex items-center gap-1.5">
                    <Icon size={12} /> {valueLabel(c)}
                  </span>
                  <span data-testid={`discount-active-badge-${c.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${c.active ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"}`}>
                    {c.active ? "Aktiv" : "Deaktiviert"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(c)} data-testid={`discount-toggle-${c.id}`} title={c.active ? "Deaktivieren" : "Aktivieren"}
                    className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${c.active ? "border-white/10 text-[#888] hover:text-red-400 hover:border-red-400/40" : "border-white/10 text-[#888] hover:text-green-400 hover:border-green-400/40"}`}>
                    <Power size={16} />
                  </button>
                  <button onClick={() => remove(c.id)} data-testid={`discount-delete-${c.id}`}
                    className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
